'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteTranslations } from '@/lib/api/translations'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteTranslationsModalProps {
	translationIds: string[]
	onClose: () => void
	onSuccess?: () => void
}

export const BulkDeleteTranslationsModal = ({
	translationIds,
	onClose,
	onSuccess,
}: BulkDeleteTranslationsModalProps) => {
	const { t } = useTranslation('languages')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const deleteTranslations = useDeleteTranslations()

	const handleDelete = async () => {
		if (!translationIds.length) return

		try {
			await deleteTranslations.mutateAsync({ ids: translationIds })

			toast({
				title: tCommon('success'),
				description: t('bulkDeleteSuccess', { count: translationIds.length }),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			// Error toast is handled by the mutation hook
			// No need to show another error toast here
		}
	}

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.bulkDelete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{t('modals.bulkDelete.description', { count: translationIds.length })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteTranslations.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteTranslations.isPending}
					>
						{deleteTranslations.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

